//Libraries
import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
  Fragment,
} from "react";
import {
  Button,
  Container,
  Grid,
  Hidden,
  Typography,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import {
  makeStyles,
  MuiThemeProvider,
  createMuiTheme,
} from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { Skeleton } from "@material-ui/lab";
import MUIDataTable from "mui-datatables";
import SearchIcon from "@material-ui/icons/Search";

//API
import {
  listAllProductsFromResellerV2,
  duplicateProducts,
} from "../api/Products";

//Custom components
import SnackbarMessage from "../components/SnackbarMessage";
import ConfirmationDialog from "../components/ConfirmationDialog";

//Styles needed for responsiveness
const useStyles = makeStyles((theme) => ({
  mainContainer: {
    [theme.breakpoints.up("lg")]: {
      paddingLeft: "190px",
    },
    [theme.breakpoints.down("xl")]: {
      marginTop: "85px",
    },
    [theme.breakpoints.between("sm", "md")]: {
      marginTop: "75px",
    },
    [theme.breakpoints.up("md")]: {
      marginTop: "65px",
    },
  },
  ListItem: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const ProductListTable = forwardRef((props, ref) => {
  const tableRef = useRef();
  const classes = useStyles();
  let history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({
    message: "",
    severity: "info",
    open: false,
  });
  const [productData, setProductdata] = useState([]);
  const [productDataBackup, setProductdataBackup] = useState([]);
  // const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [columnViewPorcentage, setColumViewPorcentage] = useState(100 / 6);
  const [searchText, setSearchText] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  // We need use useImperativeHandle in order to expose ref to getProducts for data refreshing
  useImperativeHandle(ref, () => ({
    refreshData() {
      // List users
      getAllProducts();
    },
  }));

  /**
   * Move selected products to the beginning of productData array
   * @param {Array} products - The array of products to prioritize
   * This function will reorder the productData array to prioritize selected products
   */
  const prioritizeSelectedProducts = async (
    products = [],
    selectedProducts = []
  ) => {
    if (selectedProducts.length === 0) return;
    const selectedIds = selectedProducts.map((p) => p.id);
    const reordered = [
      ...selectedProducts,
      ...products.filter((p) => !selectedIds.includes(p.id)),
    ];
    setProductdata(...[reordered]);
  };

  /**
   * Search products based on the search value
   * This function filters the productDataBackup array based on the search value
   * @param {*} searchValue
   */
  const handleSearch = (searchValue) => {
    // If search value is empty, prioritize selected products
    setSearchText(searchValue);
    // If search value is empty, show all products
    if (searchValue === "") {
      // Reset search text
      prioritizeSelectedProducts(productDataBackup, selectedProducts);
    } else {
      // Filter products based on search value
      const filteredData = productDataBackup.filter((item) => {
        return Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchValue.toLowerCase())
        );
      });
      // If there are no products found, show all products
      prioritizeSelectedProducts(filteredData, selectedProducts);
    }
  };

  /**Initial loading */
  useEffect(() => {
    // Get current page
    // const currentPage = localStorage.getItem("currentProductPage");
    // Set current page
    // if (currentPage) setPage(parseInt(currentPage));
    // Get current rows per page
    const currentRows = localStorage.getItem("currentProductRows");
    // Set current rows per page
    if (currentRows) setRowsPerPage(parseInt(currentRows));
    // TODO: With Back's settings, this may no longer be necessary, but you should first test to see if it solves the problem before removing this.
    // const resellerProducts = localStorage.getItem("resellerProducts");
    // const products = localStorage.getItem("Products");
    // const reseller = localStorage.getItem("reseller_id");
    // // Check if reseller has changed
    // if (resellerProducts && resellerProducts == reseller && products) {
    //   setProductdata(JSON.parse(products));
    //   setLoading(false);
    // }
    // // List products
    // else {
    getAllProducts();
    // }
  }, []);

  //Custom styles for text column
  const customStyle = ({ ...column }) => {
    return (
      <div
        className={classes.ListItem}
        style={{
          maxWidth:
            column && column.name == "picture"
              ? "75px"
              : column.name == "sku"
              ? "75px"
              : column.name == "product_category"
              ? "150px"
              : "100px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          fontWeight: "bold",
          padding: "10px",
          height: "10px",
        }}
      >
        {column.label}
      </div>
    );
  };

  //Custom style by columns
  const customStyleText = (value = "-") => {
    return (
      <div
        style={{
          maxWidth: "300px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {value}
      </div>
    );
  };

  //Custom style for image logo product
  const customStyleImage = (value = "") => {
    // Get image data
    let data =
      value && typeof value == "string" && value.length > 0
        ? JSON.parse(value)
        : [];
    // Get image url
    const imageUrl =
      data.length > 0 && data[0].url
        ? data[0].url
        : "/img/avid/avid-io_250x266.png";
    // Get product id
    const productId =
      data.length > 0 && data[0].product_id ? data[0].product_id : "";
    return (
      <div style={{ maxWidth: "75px" }}>
        <center>
          <img
            id={productId}
            key={productId}
            src={imageUrl}
            style={{
              maxWidth: "75%",
              height: "auto",
              maxHeight: "75px",
              padding: "0px",
            }}
          />
        </center>
      </div>
    );
  };

  //Custom style by columns
  const customStyleProCat = (value = "-") => {
    return (
      <div
        style={{
          maxWidth: "300px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {value}
      </div>
    );
  };

  //Mui Data table custom Stykes
  const theme = () =>
    createMuiTheme({
      overrides: {
        //Table head custom styles
        MUIDataTableHeadCell: {
          fixedHeader: {
            paddingTop: "0px",
            paddingBottom: "10px",
            backgroundColor: "#F2F2F2",
            borderRight: "1px solid #E5E3E3",
          },
        },
        //Search form custom style
        MUIDataTableSearch: {
          main: {
            visibility: true,
          },
        },
        //Chip filters custom styles
        MUIDataTableFilterList: {
          root: {
            marginBottom: "10px",
          },
        },
        //Filter container custom styles
        MuiGrid: {
          container: {
            display: "block",
            width: "210px",
            paddingLeft: "opx",
          },
          item: {
            boxSizing: "content-box",
          },
        },
        //Filte list options custom styles
        MUIDataTableFilter: {
          checkboxIcon: {
            padding: "0px",
          },
          checkboxFormControlLabel: {
            padding: "0px",
          },
          checkboxListTitle: {
            width: "100%",
          },
        },
        MUIDataTableSelectCell: {
          headerCell: {
            backgroundColor: "#F2F2F2",
          },
        },
        MUIDataTableToolbarSelect: {
          root: { visibility: "hidden", minHeight: "49px", display: "none" },
        },
        MuiTableHead: {
          root: {
            display: `${columnViewPorcentage ? "table-header-group" : "none"}`,
          },
        },
        MuiTableBody: {
          root: {
            display: `${columnViewPorcentage ? "table-row-group" : "none"}`,
          },
        },
        MuiTableCell: {
          root: {
            padding: "0px",
          },
        },
      },
    });

  //Columns for the table
  let initial_columns = [
    //Picture column
    {
      name: "Picture",
      label: "Picture",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleImage(value),
      },
    },
    //name column
    {
      name: "Name",
      label: "Name",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    //Sku column
    {
      name: "Sku",
      label: "SKU",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    //Division categories column
    {
      name: "Product_category",
      label: "Product Category",
      options: {
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleProCat(value),
      },
    },
    //Price column
    {
      name: "Price",
      label: "Price",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
  ];

  //Table options
  const options = {
    filter: true,
    filterType: "checkbox",
    sortFilterList: true,
    selectableRows: "multiple",
    searchPlaceholder: "Search available products",
    searchText: searchText,
    onSearchChange: (searchValue) => setSearchText(searchValue),
    textLabels: {
      body: {
        noMatch: "Sorry, no matching records found",
        toolTip: "Sort",
        columnHeaderTooltip: (column) => `${column.label}`,
      },
    },
    downloadOptions: {
      filename: "Product List",
      separator: ",",
      filterOptions: {
        useDisplayedRowsOnly: true,
      },
    },
    responsive: "standard",
    // TODO: Add onRowClick to redirect to user details page only if handleContextMenu is not working
    // onRowClick: (rowData) => {
    //   const imageInfo = rowData[0].props.children.props.children;
    //   const productId = imageInfo.key;
    //   //Redirect to product view
    //   if (productId) history.push(`/app/product/${productId}`);
    // },
    elevation: 0,
    // Custom actions for selecting rows
    onRowSelectionChange(currentRowsSelected, allRowsSelected, rowsSelected) {
      // Gets the selected users in the table
      if (
        rowsSelected &&
        Array.isArray(rowsSelected) &&
        rowsSelected.length != 0
      ) {
        // Get the selected products from the table
        let products = [];
        // Loop through the selected rows
        for (const index of rowsSelected) {
          // Get the product data from the productData array
          const product = productData[index];
          // Check if product is already selected
          if (!products.some((p) => p.id === product.id)) {
            // If not, add the product to the selected products array
            products.push(product);
          }
        }
        // If the search text is not empty and no products are selected, clear the search text
        // This is to avoid showing products that are not in the search results
        // when the user selects a product from the table
        if (searchText !== "" && selectedProducts.length === 0) {
          if (tableRef.current) {
            const toolbar = tableRef.current.querySelector(".MuiToolbar-root");
            const searchField = toolbar.querySelector('input[type="search"]');
            if (searchField) {
              searchField.blur(); // Remove focus from the search field
            }
            // Alternativa: cambiar el estado para cerrar el search
            setSearchText(""); // Clear the search text
          }
        }
        // Set the selected products state
        setSelectedProducts([...products]);
        prioritizeSelectedProducts(productData, products);
      } else {
        // If no products are selected, clear the selected products state
        setSelectedProducts([...[]]);
        prioritizeSelectedProducts(productDataBackup, []);
      }
    },
    rowsSelected: productData
      .map((item, index) =>
        selectedProducts.map((product) => product.id).includes(item.id)
          ? index
          : null
      )
      .filter((index) => index !== null),
    onTableChange: (action, state) => {
      // columns porcetage views
      if (action == "viewColumnsChange") {
        const columns = state.columns;
        let countColumns = columns.length;
        let showColumns = 0;
        for (const column of columns) {
          if (column.display == "true") {
            showColumns++;
          }
        }
        if (showColumns !== columns.length) {
          countColumns = showColumns;
        }
        const columnViewPorcentage =
          countColumns > 0 ? 100 / countColumns : null;
        setColumViewPorcentage(columnViewPorcentage);
      }
    },
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [10, 20, 50, 100],
    // page: page,
    // onChangePage: (currentPage) => {
    // localStorage.setItem("currentProductPage", currentPage);
    // setPage(currentPage);
    // },
    // onChangeRowsPerPage: (numberOfRows) => {
    //   localStorage.setItem("currentProductRows", numberOfRows);
    //   setRowsPerPage(numberOfRows);
    //   setPage(0);
    // },
  };

  /**
   * Close snackbar
   */
  const handleClose = () => {
    setError({
      open: false,
      message: "",
      severity: "info",
    });
    setContextMenu(null);
    setSelectedRow(null);
  };

  /**
   * Get all products from current reseller
   */
  const getAllProducts = () => {
    setLoading(true);
    listAllProductsFromResellerV2()
      .then((result) => {
        if (result.data.getProductsTable) {
          const products = result.data.getProductsTable;
          const reseller = localStorage.getItem("reseller_id");
          localStorage.setItem("Products", JSON.stringify(products));
          localStorage.setItem("resellerProducts", reseller);
          setProductdata(products);
          setProductdataBackup(products);
        } else {
          let error_message = result.errors
            ? result.errors[0].message
            : "An error occurred";
          // Display error
          setError({
            open: true,
            message: `${error_message}. Please try again later.`,
            severity: "error",
          });
        }
      })
      .catch(() => {
        // Display error
        setError({
          open: true,
          message: `An error occurred while exporting products`,
          severity: "error",
        });
      })
      .finally(() => setLoading(false)); // Finally, change loading flag
  };

  /**
   * Function to handle right click on table row
   * @param {MouseEvent} event - The mouse event object
   */
  const handleContextMenu = (event) => {
    // Search the closest td element
    const td = event.target.closest("td");
    // If the td is found, get the input checkbox
    if (td) {
      // Search for the checkbox input inside the td (it may be nested within several div/span)
      const checkbox = td.querySelector('input[type="checkbox"]');
      if (checkbox) {
        // If the checkbox was found, do not show the context menu
        return;
      }
    }
    // Find the closest table row
    const tr = event.target.closest("tr");
    // Find the first image in the row
    const img = tr ? tr.querySelector("img") : null;
    // Get the image id
    const imageId = img ? img.id : null;
    // Check if the selected row is valid
    if (!imageId) {
      return;
    }
    // Prevent the default context menu from appearing
    event.preventDefault();
    if (event.ctrlKey || event.metaKey || event.type === "contextmenu") {
      // Set the selected row and context menu position
      setSelectedRow(imageId);
      // Set the context menu position
      setContextMenu({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
      });
    } else if (event.type === "click") {
      history.push(`/app/product/${imageId}`);
    }
  };

  /**
   * Open new tab with product details
   * @param {boolean} openNewTab - If true, open in new tab, else copy link to clipboard
   * @param {*} openNewTab
   */
  const handleOpenInNewTab = (openNewTab = true) => {
    // Check if selectedRow is valid
    if (selectedRow) {
      // Get the product ID from the selected row
      const productId = selectedRow;
      // Open the new tab with the product details page
      const url = `/app/product/${productId}`;
      // Check if openNewTab is true
      if (openNewTab) {
        // Open the URL in a new tab
        window.open(url, "_blank");
      } else {
        // Copy the URL to the clipboard
        navigator.clipboard
          .writeText(`${process.env.REACT_APP_WEB}${url}`)
          .then(() => {
            setError({
              open: true,
              message: `Link copied to clipboard`,
              severity: "success",
            });
          });
      }
    }
    handleClose();
  };

  /**
   * Handle duplicate products
   */
  const handleDuplicateProducts = () => {
    // Get the selected products ids
    const prodcutIds = selectedProducts.map((product) => product.id);
    // Check if there are selected products
    if (prodcutIds.length === 0) {
      setError({
        open: true,
        message: "No products selected to duplicate",
        severity: "warning",
      });
      return;
    }
    // Call the duplicateProducts API
    duplicateProducts(prodcutIds)
      .then((result) => {
        if (result.data.duplicateProducts) {
          // Clear selected products
          setSelectedProducts([]);
          // Refresh the product list
          getAllProducts();
          // Show success message
          setError({
            open: true,
            message: "Products duplicated successfully",
            severity: "success",
          });
        } else {
          let error_message = result.errors
            ? result.errors[0].message
            : "An error occurred";
          // Display error
          setError({
            open: true,
            message: `${error_message}. Please try again later.`,
            severity: "error",
          });
          // If there is an error, open the duplicate modal
          setOpenDuplicateModal(true);
        }
      })
      .catch(() => {
        // Display error
        setError({
          open: true,
          message: `An error occurred while duplicating products`,
          severity: "error",
        });
        // If there is an error, open the duplicate modal
        setOpenDuplicateModal(true);
      });
  };

  return (
    <Container className={classes.mainContainer} maxWidth="xl">
      <Grid container style={{ padding: "20px" }}>
        <Grid item xs={12} sm={12} md={6} lg={7}>
          <Typography variant="h4" gutterBottom>
            Products
          </Typography>
        </Grid>
        {/**Skeleton loading */}
        {loading ? (
          <React.Fragment>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Hidden smDown>
                <Skeleton
                  variant="retangular"
                  height={39}
                  style={{ float: "right" }}
                  width={200}
                />
              </Hidden>
              <Hidden mdUp>
                <Skeleton
                  variant="retangular"
                  height={39}
                  style={{ float: "right" }}
                  width={"100%"}
                />
              </Hidden>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={2}>
              <Hidden smDown>
                <Skeleton
                  variant="retangular"
                  height={39}
                  style={{ float: "right" }}
                  width={150}
                />
              </Hidden>
              <Hidden mdUp>
                <Skeleton
                  variant="retangular"
                  height={39}
                  style={{ float: "right" }}
                  width={"100%"}
                />
              </Hidden>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={9}>
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      style={{ margin: "3px" }}
                      width={120}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    style={{ paddingRight: "25px", marginBottom: "20px" }}
                  >
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      style={{ margin: "3px", float: "right" }}
                      width={40}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      style={{ margin: "3px", float: "right" }}
                      width={40}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      style={{ margin: "3px", float: "right" }}
                      width={40}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      style={{ margin: "3px", float: "right" }}
                      width={40}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      style={{ margin: "3px", float: "right" }}
                      width={40}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((row, i) => {
                    return (
                      <Grid key={i} container>
                        <Grid item xs={1}>
                          <Skeleton
                            variant="rectangular"
                            height={30}
                            style={{ margin: "3px", marginBottom: "15px" }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Skeleton
                            variant="rectangular"
                            height={30}
                            style={{ margin: "3px" }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Skeleton
                            variant="rectangular"
                            height={30}
                            style={{ margin: "3px" }}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Skeleton
                            variant="rectangular"
                            height={30}
                            style={{ margin: "3px" }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Skeleton
                            variant="rectangular"
                            height={30}
                            style={{ margin: "3px" }}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Grid
              container
              spacing={2}
              item
              sm={12}
              md={6}
              lg={5}
              style={{ margin: "0px" }}
            >
              {/* <Hidden lgDown>
                <Grid item xl={4} />
              </Hidden> */}
              {/**Duplicate Products Button */}
              <Grid item xs={12} sm={12} md={2} lg={2} xl={3}>
                {selectedProducts && selectedProducts.length != 0 && (
                  <Button
                    variant="text"
                    color="primary"
                    fullWidth
                    onClick={() => setOpenDuplicateModal(true)}
                  >
                    DUPLICATE
                  </Button>
                )}
              </Grid>
              {/**Add attributes templates button */}
              <Grid item xs={12} sm={12} md={5} lg={5} xl={6}>
                <Button
                  variant="contained"
                  color="default"
                  onClick={() => history.push("/app/attributes/templates")}
                  style={{ float: "right" }}
                  fullWidth
                >
                  ATTRIBUTES TEMPLATES
                </Button>
              </Grid>
              {/**Add products button */}
              <Grid item xs={12} sm={12} md={5} lg={5} xl={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => history.push("/app/add-product")}
                  fullWidth
                >
                  ADD PRODUCT
                </Button>
              </Grid>
            </Grid>
            {/**Seach product, this only appear when products were selected */}
            {selectedProducts && selectedProducts.length != 0 && (
              <Grid
                container
                spacing={2}
                style={{ padding: "0px 24px", marginTop: "27px" }}
              >
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    variant="standard"
                    size="small"
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search available products"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}></Grid>
              </Grid>
            )}
            {/**Table container */}
            <Grid container>
              {/**Table */}
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                className={classes.ListItem}
                style={{ marginTop: "15px" }}
              >
                <MuiThemeProvider theme={theme}>
                  <div
                    onContextMenu={handleContextMenu}
                    onClick={handleContextMenu}
                  >
                    <MUIDataTable
                      data={productData}
                      columns={initial_columns}
                      options={options}
                    />
                  </div>
                  {/**Menu - Open new tab */}
                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contextMenu
                        ? {
                            top: contextMenu.mouseY,
                            left: contextMenu.mouseX,
                          }
                        : undefined
                    }
                    autoFocus={false}
                    disableAutoFocusItem
                    disableRestoreFocus
                  >
                    <MenuItem onClick={handleOpenInNewTab}>
                      Open link in a new tab
                    </MenuItem>
                    <MenuItem onClick={() => handleOpenInNewTab(false)}>
                      Copy link
                    </MenuItem>
                  </Menu>
                </MuiThemeProvider>
              </Grid>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      <SnackbarMessage
        open={error.open}
        severity={error.severity}
        message={error.message}
        handleClose={handleClose}
      />
      {openDuplicateModal && (
        <ConfirmationDialog
          open={openDuplicateModal}
          dialogTitle="Duplicate Product"
          dialogContentText={
            <Fragment>
              <Typography variant="body1">
                The following selected products will be duplicated.
              </Typography>
              <div
                style={{
                  margin: "10px 0px",
                  maxHeight: selectedProducts.length > 10 ? "200px" : "auto",
                  overflowY: selectedProducts.length > 10 ? "auto" : "visible",
                }}
              >
                {selectedProducts.map((product, index) => (
                  <Typography key={index} variant="body2">
                    {product.Name && product.Name.length > 55
                      ? product.Name.substring(0, 55) + "..."
                      : product.Name}
                  </Typography>
                ))}
              </div>
              <Typography variant="body1">Do you want to continue?</Typography>
            </Fragment>
          }
          closeButtonText="Cancel"
          confirmButtonText="Continue"
          confirmButtonColor="primary"
          confirmButtonClick={() => handleDuplicateProducts()}
          handleClose={() => setOpenDuplicateModal(false)}
          PropsButton={{ size: "medium" }}
        />
      )}
    </Container>
  );
});

ProductListTable.propTypes = {};

export default ProductListTable;
